/** Peças pequenas partilhadas pelas páginas. */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { Check, ChevronsUpDown, Loader2, Upload } from "lucide-react"
import { Button, type ButtonProps } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export function Page({ children }: { children: ReactNode }) {
  return <div className="mx-auto flex max-w-[1500px] flex-col gap-4 p-4">{children}</div>
}

export function Panel({ title, subtitle, actions, children, className }: { title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <section className={cn("rounded-xl border bg-card p-4 shadow-sm", className)}>
      {(title || actions) && (
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 max-w-[90ch]">
            {title && <h2 className="text-[15px] font-semibold">{title}</h2>}
            {subtitle && <div className="text-[13px] text-muted-foreground">{subtitle}</div>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  )
}

const TAG: Record<string, string> = {
  ok: "border-green-200 bg-green-50 text-green-700",
  warn: "border-amber-200 bg-amber-50 text-amber-800",
  bad: "border-red-200 bg-red-50 text-red-700",
  info: "border-blue-200 bg-blue-50 text-blue-700",
  ai: "border-orange-200 bg-orange-50 text-orange-700",
  neutral: "border-border bg-muted text-foreground",
}

export function Tag({ tone = "neutral", children, title }: { tone?: keyof typeof TAG; children: ReactNode; title?: string }) {
  return (
    <span title={title} className={cn("inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium leading-none", TAG[tone])}>
      {children}
    </span>
  )
}

export function Note({ children }: { children: ReactNode }) {
  return <p className="m-0 mt-3 max-w-[90ch] text-xs text-muted-foreground">{children}</p>
}

export function Field({ label, children, hint, className }: { label: string; children: ReactNode; hint?: ReactNode; className?: string }) {
  return (
    <label className={cn("flex flex-col gap-1 text-xs font-medium text-muted-foreground", className)}>
      {label}
      {children}
      {hint && <span className="font-normal text-[11px]">{hint}</span>}
    </label>
  )
}

export const inputCls =
  "h-8 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"

/** Botão que corre uma ação assíncrona com spinner. */
export function AsyncButton({ onRun, children, ...props }: Omit<ButtonProps, "onClick"> & { onRun: () => Promise<unknown> }) {
  const [busy, setBusy] = useState(false)
  return (
    <Button
      {...props}
      disabled={busy || props.disabled}
      onClick={async () => {
        setBusy(true)
        try {
          await onRun()
        } finally {
          setBusy(false)
        }
      }}
    >
      {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {children}
    </Button>
  )
}

/** Botão que abre a escolha de ficheiro (com `onFiles`, aceita vários de uma vez). */
export function FileButton({
  accept,
  onFile,
  onFiles,
  children,
  ...props
}: Omit<ButtonProps, "onClick"> & { accept: string; onFile?: (f: File) => Promise<unknown>; onFiles?: (fs: File[]) => Promise<unknown> }) {
  const ref = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={accept}
        multiple={Boolean(onFiles)}
        className="hidden"
        onChange={async (e) => {
          const files = [...(e.target.files ?? [])]
          e.target.value = ""
          if (!files.length) return
          setBusy(true)
          try {
            if (onFiles) await onFiles(files)
            else await onFile?.(files[0])
          } finally {
            setBusy(false)
          }
        }}
      />
      <Button {...props} disabled={busy || props.disabled} onClick={() => ref.current?.click()}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {children}
      </Button>
    </>
  )
}

export function Loading() {
  return (
    <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> A carregar…
    </div>
  )
}

export function ErrorBox({ error }: { error: string }) {
  return <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
}

export function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return <th className={cn("whitespace-nowrap border-b px-2 py-2 text-left text-[11.5px] font-semibold text-muted-foreground", className)}>{children}</th>
}

export function Td({ children, className, colSpan }: { children?: ReactNode; className?: string; colSpan?: number }) {
  return <td colSpan={colSpan} className={cn("border-b px-2 py-2 align-top text-[13px]", className)}>{children}</td>
}

export interface SelectOption {
  value: string
  label: string
}

/** Sem maiúsculas nem acentos: «evora» encontra «Évora». */
const fold = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()

/**
 * Dropdown com pesquisa (em vez do `<select>` nativo, que numa lista de 300
 * concelhos obriga a fazer scroll). `allLabel` é a opção «todos» (valor "").
 * Teclado: ↑/↓ escolhem, Enter confirma, Esc fecha. `placement="top"` abre
 * para cima (no fundo de um painel).
 */
export function SearchSelect({
  value,
  onChange,
  options,
  allLabel,
  placeholder = "Procurar…",
  ariaLabel,
  className,
  placement = "bottom",
}: {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  allLabel?: string
  placeholder?: string
  ariaLabel?: string
  className?: string
  placement?: "bottom" | "top"
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState("")
  const [active, setActive] = useState(0)
  const box = useRef<HTMLDivElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const search = useRef<HTMLInputElement>(null)

  const all = useMemo(() => (allLabel !== undefined ? [{ value: "", label: allLabel }, ...options] : options), [allLabel, options])
  const shown = useMemo(() => {
    const needle = fold(q.trim())
    return needle ? all.filter((o) => fold(o.label).includes(needle)) : all
  }, [all, q])
  const current = all.find((o) => o.value === value)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onDown)
    return () => document.removeEventListener("mousedown", onDown)
  }, [open])

  // Foco sem deslocar a página (o autoFocus fazia saltar o painel).
  useEffect(() => {
    if (open) search.current?.focus({ preventScroll: true })
  }, [open])

  useEffect(() => {
    const el = list.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)
    const box = list.current
    if (!el || !box) return
    if (el.offsetTop < box.scrollTop) box.scrollTop = el.offsetTop
    else if (el.offsetTop + el.offsetHeight > box.scrollTop + box.clientHeight) box.scrollTop = el.offsetTop + el.offsetHeight - box.clientHeight
  }, [active, open])

  function show() {
    setQ("")
    setActive(Math.max(0, all.findIndex((o) => o.value === value)))
    setOpen(true)
  }

  function pick(v: string) {
    onChange(v)
    setOpen(false)
  }

  return (
    <div ref={box} className={cn("relative", className)}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(inputCls, "flex items-center justify-between gap-1 text-left text-[inherit]")}
        onClick={() => (open ? setOpen(false) : show())}
      >
        <span className={cn("truncate", !value && "text-muted-foreground")}>{current?.label ?? allLabel ?? "—"}</span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
      </button>
      {open && (
        <div className={cn("absolute left-0 z-50 flex w-full min-w-[200px] flex-col rounded-md border bg-popover text-popover-foreground shadow-lg", placement === "top" ? "bottom-full mb-1" : "top-full mt-1")}>
          <input
            ref={search}
            className="h-8 border-b bg-transparent px-2 text-[13px] outline-none"
            placeholder={placeholder}
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setActive(0)
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault()
                setActive((i) => Math.min(i + 1, shown.length - 1))
              } else if (e.key === "ArrowUp") {
                e.preventDefault()
                setActive((i) => Math.max(i - 1, 0))
              } else if (e.key === "Enter") {
                e.preventDefault()
                if (shown[active]) pick(shown[active].value)
              } else if (e.key === "Escape") {
                e.stopPropagation()
                setOpen(false)
              }
            }}
          />
          <div ref={list} role="listbox" className="relative max-h-64 overflow-auto py-1">
            {shown.map((o, i) => (
              <div
                key={o.value || "__all"}
                data-i={i}
                role="option"
                aria-selected={o.value === value}
                className={cn("flex cursor-pointer items-center gap-1.5 px-2 py-1 text-[13px]", i === active && "bg-accent/15", !o.value && "text-muted-foreground")}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(o.value)
                }}
              >
                <Check className={cn("h-3.5 w-3.5 shrink-0", o.value === value ? "opacity-100" : "opacity-0")} />
                <span className="truncate">{o.label}</span>
              </div>
            ))}
            {shown.length === 0 && <div className="px-2 py-2 text-[12.5px] text-muted-foreground">Nada encontrado.</div>}
          </div>
        </div>
      )}
    </div>
  )
}
